// eslint-disable-next-line
import { RouteMeta } from "vue-router";
import Countly from "countly-sdk-web";
import { countlyVariables } from "@/constants";
import { getUrlQuery } from "@/utils/utm";

Countly.q = Countly.q || [];

const isTrackingDisabled = window.location.hostname === "localhost";
const isProd = window.location.hostname === "flex.countly.com";

const SURVEY_WIDGET = {
  dev: "67a5edf9864c1f3ad7bc1c06",
  prod: "67a5edf9864c1f3ad7bc1c06",
};

// Shown right after a user terminates a server from the server detail page.
// This widget lives in the WORKSPACE app (countlyVariables.DEV_WS_APP_KEY /
// PROD_WS_APP_KEY), so presentSurvey() must be told to target that app.
const SERVER_TERMINATION_SURVEY_WIDGET = {
  dev: "6aa00c1a6507ad3b6ff6ffd5",
  prod: "6aa00c1a6507ad3b6ff6ffd5",
};

const USER_SPECIFIC_EVENT_KEYS = [
  "signupBegin",
  "signupUser",
  "signupVerify",
  "signupWorkspace",
  "signup",
  "signupCompleteAction",
  "signIn",
  "resendVerificationCode",
  "serverNewButton",
];

const LANDING_EVENT_KEYS = [...USER_SPECIFIC_EVENT_KEYS];

// disabled temporarly: 'track_errors'
const FLEX_TRACKING_TYPES = [
  "track_sessions",
  "track_pageview",
  "track_clicks",
  "track_scrolls",
  "track_links",
];

type AnalyticsAppOptions = {
  isMain?: boolean;
  enabledTrackingTypes?: Array<string>;
};

export enum AnalyticsAppType {
  USER = "user",
  WORKSPACE = "workspace",
  LANDING = "landing",
}

class AnalyticsApp {
  private flagEnabled = false;
  private flagInitialized = false;

  constructor(public name: string, public config: any, private options: AnalyticsAppOptions = {}) {
    this.name = name;
    this.config = config;
    this.options = {
      ...{ isMain: false, ignoredEventKeys: [], enabledTrackingTypes: [] },
      ...options,
    };
  }

  public init(enabled: boolean, deviceId?: string) {
    if (isTrackingDisabled) {
      return;
    }
    if (this.options.isMain) {
      Countly.init({
        ...this.config,
        device_id: deviceId,
      });
    } else {
      Countly.q.push([
        "init",
        {
          ...this.config,
          device_id: deviceId,
        },
      ]);
    }

    // enable in app messaging
    if (this.options.isMain) {
      Countly.content.enterContentZone();
    } else {
      Countly.q.push([this.config.app_key, "content.enterContentZone"]);
    }

    if (this.options.enabledTrackingTypes) {
      this.options.enabledTrackingTypes.forEach((trackType) => {
        if (this.options.isMain) {
          Countly.q.push([trackType]);
        } else {
          Countly.q.push([this.config.app_key, trackType]);
        }
      });
    }

    this.flagInitialized = true;
    this.flagEnabled = enabled;
  }

  public setDeviceId(deviceId: string, shouldUserMerge?: boolean) {
    if (isTrackingDisabled) {
      return;
    }
    if (deviceId) {
      if (!this.isEnabled) {
        // auto enables and initializes lazy apps
        this.init(true, deviceId);
      } else if (this.options.isMain) {
        Countly.q.push(["change_id", deviceId, shouldUserMerge || false]);
      } else {
        Countly.q.push([this.config.app_key, "change_id", deviceId, shouldUserMerge || false]);
      }
    }
  }

  public setUserProfile(profile: any) {
    if (isTrackingDisabled || !this.flagInitialized || !this.flagEnabled) {
      return false;
    }
    if (this.options.isMain) {
      Countly.q.push(["user_details", profile]);
    } else {
      Countly.q.push([this.config.app_key, "user_details", profile]);
    }
    return true;
  }

  public push(arr: Array<any>): boolean {
    if (isTrackingDisabled || !this.flagInitialized || !this.flagEnabled) {
      return false;
    }
    if (this.options.isMain) {
      Countly.q.push(arr);
    } else {
      Countly.q.push([this.config.app_key, ...arr]);
    }
    return true;
  }

  public enable() {
    if (isTrackingDisabled) {
      return;
    }
    if (!this.flagInitialized) {
      this.init(true);
      return;
    }
    this.flagEnabled = true;
  }

  public disable() {
    this.flagEnabled = false;
  }

  public get isEnabled() {
    return this.flagEnabled;
  }
}

class AnalyticsManager {
  public remoteConfig = {};

  private apps: Record<AnalyticsAppType, AnalyticsApp> = {
    // User app
    [AnalyticsAppType.USER]: new AnalyticsApp(
      AnalyticsAppType.USER,
      {
        app_key: isProd ? countlyVariables.PROD_APP_KEY : countlyVariables.DEV_APP_KEY,
        url: countlyVariables.SERVER_URL,
        namespace: countlyVariables.NAMESPACE,
        remote_config: countlyVariables.REMOTE_CONFIG,
        require_consent: countlyVariables.REQUIRE_CONSENT,
        force_post: true,
        getSearchQuery: getUrlQuery,
        getViewUrl: () => window.location.pathname + window.location.hash + window.location.search,
      },
      {
        isMain: true,
        enabledTrackingTypes: FLEX_TRACKING_TYPES,
      },
    ),

    // Workspace app
    [AnalyticsAppType.WORKSPACE]: new AnalyticsApp(
      AnalyticsAppType.WORKSPACE,
      {
        app_key: isProd ? countlyVariables.PROD_WS_APP_KEY : countlyVariables.DEV_WS_APP_KEY,
        url: countlyVariables.SERVER_URL,
        getSearchQuery: getUrlQuery,
        getViewUrl: () => window.location.pathname + window.location.hash + window.location.search,
      },
      {
        enabledTrackingTypes: FLEX_TRACKING_TYPES,
      },
    ),

    // Landing app
    [AnalyticsAppType.LANDING]: new AnalyticsApp(
      AnalyticsAppType.LANDING,
      {
        app_key: isProd
          ? countlyVariables.PROD_LANDING_APP_KEY
          : countlyVariables.DEV_LANDING_APP_KEY,
        url: countlyVariables.SERVER_URL,
        getSearchQuery: getUrlQuery,
      },
      {
        enabledTrackingTypes: ["track_sessions"],
      },
    ),
  };

  public init() {
    if (isTrackingDisabled) return;

    // @ts-ignore
    if (!window.Countly) {
      this.apps.user.init(true);
      // workspace and landing apps are initiated on setDeviceId
    }
  }

  private _push(arr: Array<any>, workspace = true, landing = false) {
    if (isTrackingDisabled) return;
    this.apps.user.push(arr);

    if (workspace) {
      this.apps.workspace.push(arr);
    }

    if (landing) {
      this.apps.landing.push(arr);
    }
  }

  disableAnalytics(app: AnalyticsAppType) {
    this.apps[app].disable();
  }

  setCustomUserData(key: string, value: any) {
    this._push(["userData.set", key, value]);
    this._push(["userData.save"]);
  }

  setDeviceId(app: AnalyticsAppType, deviceId: string, shouldUserMerge: boolean) {
    this.apps[app].setDeviceId(
      deviceId,
      app === AnalyticsAppType.LANDING ? false : shouldUserMerge,
    );
  }

  setUserProfile(app: AnalyticsAppType, profile: any) {
    this.apps[app].setUserProfile(profile);
  }

  trackEvent(
    eventKey: string,
    eventOptions?: {
      count?: number;
      sum?: number;
      dur?: number;
      segmentation?: { [key: string]: any };
    },
  ) {
    this._push(
      [
        "add_event",
        {
          key: eventKey,
          ...eventOptions,
        },
      ],
      !USER_SPECIFIC_EVENT_KEYS.includes(eventKey),
      LANDING_EVENT_KEYS.includes(eventKey),
    );
  }

  // A survey widget belongs to exactly one Countly app, so this pushes to that
  // app only. The id must belong to the app it is pushed to: showSurvey() falls
  // back to the app's FIRST available survey when the id is not in its widget
  // list, which silently shows an unrelated survey instead of failing.
  private presentSurvey(widgetId: string, app: AnalyticsAppType = AnalyticsAppType.USER) {
    if (isTrackingDisabled || !widgetId) return;
    this.apps[app].push(["feedback.showSurvey", widgetId]);
  }

  showSurveyWidget() {
    this.presentSurvey(SURVEY_WIDGET[isProd ? "prod" : "dev"]);
  }

  showServerTerminationSurvey() {
    this.presentSurvey(
      SERVER_TERMINATION_SURVEY_WIDGET[isProd ? "prod" : "dev"],
      AnalyticsAppType.WORKSPACE,
    );
  }

  // Debug helpers, exposed on window as __flexAnalytics, so a survey can be
  // triggered from devtools instead of by replaying the flow that owns it —
  // terminating a real server, for instance. Exposed in production too, because
  // the survey widgets only exist in the production apps. Nothing here is
  // privileged: the app keys already ship in the bundle.
  // A widget id only resolves in the app it was created in; debugListWidgets()
  // prints what a given app actually has for the current device.
  debugShowSurvey(widgetId: string, app: AnalyticsAppType = AnalyticsAppType.USER) {
    if (isTrackingDisabled) {
      // eslint-disable-next-line no-console
      console.warn("[analytics] tracking is disabled on localhost, nothing will be shown");
      return;
    }
    this.presentSurvey(widgetId, app);
  }

  debugListWidgets(app: AnalyticsAppType = AnalyticsAppType.USER) {
    const pushed = this.apps[app].push([
      "get_available_feedback_widgets",
      (widgets: Array<any>, error: any) => {
        // eslint-disable-next-line no-console
        console.log(`[analytics] ${app} widgets`, widgets, error);
      },
    ]);
    if (!pushed) {
      // eslint-disable-next-line no-console
      console.warn(`[analytics] ${app} app is not initialized/enabled, nothing was requested`);
    }
  }

  trackError(error: any) {
    this._push(["log_error", error]);
  }

  trackPageView(viewName: string) {
    this._push(["track_pageview", viewName]);
  }

  eventSucceed(key: string, _segmentation?: object) {
    if (isTrackingDisabled) return;
    let segmentation = {
      Result: "Success",
    };
    if (_segmentation) {
      segmentation = { ...segmentation, ..._segmentation };
    }
    this.trackEvent(key, {
      segmentation,
    });
  }

  eventFailed(key: string, failureReason: string) {
    this.trackEvent(key, {
      segmentation: {
        Result: "Failure",
        FailureReason: failureReason,
      },
    });
  }

  startTimedEvent(key: string) {
    this._push(["start_event", key], !USER_SPECIFIC_EVENT_KEYS.includes(key));
  }

  endTimedEvent(key: string, eventObject?: object) {
    if (eventObject) {
      this._push(["end_event", eventObject], !USER_SPECIFIC_EVENT_KEYS.includes(key));
    } else {
      this._push(["end_event", key], !USER_SPECIFIC_EVENT_KEYS.includes(key));
    }
  }

  asObject() {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const instance = this;
    const originalClass: any = instance || {};
    const keys = Object.getOwnPropertyNames(Object.getPrototypeOf(originalClass));
    return keys.reduce((classAsObj: any, key) => {
      if (key === "constructor") return classAsObj;
      // eslint-disable-next-line no-param-reassign
      classAsObj[key] = originalClass[key].bind(instance);
      return classAsObj;
    }, {});
  }
}

export const analyticsManager = new AnalyticsManager();
analyticsManager.init();

(window as any).__flexAnalytics = analyticsManager;

export const pageViewTracker = (to: RouteMeta, from: RouteMeta, next: any) => {
  if (to.name) {
    analyticsManager.trackPageView(to.name);
  }
  next();
};

export const errorTracker = (error: any) => {
  analyticsManager.trackError(error);
};

export const AnalyticsMixin = {
  setup() {
    return { ...analyticsManager.asObject() };
  },
};
